import React from 'react';
import { motion } from 'framer-motion';
import { Github, Linkedin, Mail, Heart } from 'lucide-react';
import { usePortfolioData } from '../utils/usePortfolioData';

const Footer: React.FC = () => {
  const { profile } = usePortfolioData();
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Home', href: '#home' },
    { name: 'About', href: '#about' },
    { name: 'Projects', href: '#projects' },
    { name: 'Experience', href: '#experience' },
    { name: 'Writeups', href: '#writeups' },
    { name: 'Contact', href: '#contact' },
  ];

  const socialLinks = [
    { name: 'GitHub', href: profile?.socialLinks?.github || 'https://github.com/Anup0118', icon: Github },
    { name: 'LinkedIn', href: profile?.socialLinks?.linkedin || 'https://www.linkedin.com/in/adhikarianup', icon: Linkedin },
    { name: 'Email', href: `mailto:${profile?.socialLinks?.email || 'adhikari.anup2018@gmail.com'}`, icon: Mail },
  ];

  const scrollToSection = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-cyber-darker border-t border-cyber-blue/20">
      <div className="container-custom">
        <div className="py-16">
          <div className="grid md:grid-cols-4 gap-12">
            {/* Brand */}
            <div className="md:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="space-y-6"
              >
                <h3 className="text-2xl font-bold gradient-text">{profile?.name || 'Anup Adhikari'}</h3>
                <p className="text-gray-400 max-w-md text-lg leading-relaxed">
                  Cybersecurity enthusiast and ethical hacker dedicated to securing the digital world. 
                  Founding member of Y3ti@Sec and passionate about continuous learning.
                </p>
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <span>Made with</span>
                  <Heart size={16} className="text-red-400" />
                  <span>for the cybersecurity community</span>
                </div>
              </motion.div>
            </div>

            {/* Quick Links */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h4 className="text-lg font-semibold text-white">Quick Links</h4>
              <ul className="space-y-3">
                {quickLinks.map((link) => (
                  <li key={link.name}>
                    <button
                      onClick={() => scrollToSection(link.href)}
                      className="text-gray-400 hover:text-cyber-blue transition-colors duration-200 text-sm"
                    >
                      {link.name}
                    </button>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Social Links */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h4 className="text-lg font-semibold text-white">Connect</h4>
              <div className="flex flex-col space-y-3">
                {socialLinks.map((social) => (
                  <a
                    key={social.name}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-gray-400 hover:text-cyber-blue transition-colors duration-200 text-sm"
                  >
                    <social.icon size={16} />
                    <span>{social.name}</span>
                  </a>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Copyright */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
            className="border-t border-cyber-blue/20 mt-12 pt-8 text-center"
          >
            <p className="text-gray-400 text-sm">
              © {currentYear} {profile?.name || 'Anup Adhikari'}. All rights reserved.
            </p>
          </motion.div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 